import React, { useState, useEffect } from "react";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import DatePickerField from "../../../common/DatePickerDashboard";
import NoDataFound from "../../../common/NoDataFound";
import {
  Typography,
  Button,
  FormControl,
  Select,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchQuoteFilteredDataAction,
  setDashboardData,
} from "../../../redux/actions/dashboardAction";
import { getGeneralPartyInfoByIdDashboard, clearGpId, updateGeneralPartyInfo } from "../../../redux/actions/generalPartyInfoActions";
import { useNavigate } from "react-router-dom";
import Loader from "../../loader/loader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloseIcon from '@mui/icons-material/Close';
import DatePickerBot from "../../bot/DatePickerBot";

import ReusableTable from "../../../common/ReusableTable";

const QuoteTableData = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOption, setSelectedOption] = useState("today");
  const [customDate, setCustomDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [generalData, setGeneralData] = useState(null);

  const getToday = () => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}`
    );
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`);
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
      2,
      "0"
    )}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

 
  const parseQuoteDate = (dateString) => {
    if (!dateString) return null;

  
    let parsedDate;

   
    if (dateString.includes("-") && dateString.split("-").length === 3) {
      const parts = dateString.split("-");
      if (parts[0].length === 2) {
        
        parsedDate = dayjs(`${parts[2]}-${parts[0]}-${parts[1]}`);
      } else {
        
        parsedDate = dayjs(dateString);
      }
    } else {
    
      parsedDate = dayjs(dateString);
    }

    return parsedDate.isValid() ? parsedDate : null;
  };

  
  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleProceedWithExistingDate = () => {
    setOpenDialog(false);
    proceedToNavigation(selectedQuote, generalData);
  };

 
  const handleSetEffectiveDate = () => {
    let newEffDate;
    if (selectedOption === "today") {
      newEffDate = dayjs().format("MM-DD-YYYY");
    } else if (selectedOption === "tomorrow") {
      newEffDate = dayjs().add(1, "day").format("MM-DD-YYYY");
    } else {
      newEffDate = dayjs(customDate).format("MM-DD-YYYY");
    }

    
    const updateFormData = {
      ...generalData,
      effectiveDate: newEffDate,
    };

    
    dispatch(
      updateGeneralPartyInfo(updateFormData, () => {
        const updatedGeneralData = { ...generalData, effectiveDate: newEffDate };        
        const updatedQuote = { ...selectedQuote, effDate: newEffDate };
        setOpenDialog(false);        
        proceedToNavigation(updatedQuote, updatedGeneralData);
      })
    );
  };

  // Function to proceed with navigation
  const proceedToNavigation = (quote, generalData) => {
    if (!quote?.quoteNumber || !generalData) {
      console.error("quoteNumber or generalData is undefined or missing.");
      return;
    }

    navigate("/mainview", { state: { generalData, updatedQuote: quote } });
  };

  const loading = useSelector((state) => state.dashboardData.loading);

  const quoteData = useSelector((state) => {
    const quotes = state.dashboardData.quotes;
    if (Array.isArray(quotes)) return quotes;
    if (quotes && Array.isArray(quotes.quotesList)) return quotes.quotesList;
    return [];
  });

  const paginationData = useSelector((state) => {
    const quotes = state.dashboardData.quotes;
    if (quotes && quotes.pageable) return quotes.pageable;
    return { totalElements: 0, totalPages: 0, pageNumber: 0 };
  });

  useEffect(() => {
    setFilteredData(quoteData || []);
  }, [quoteData]);

  const handleApply = () => {
    if (fromDate && toDate) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        status: selectedFilter,
      };

      setPage(0);
      if ((fromDate && toDate) || selectedFilter !== "All") {
        dispatch(fetchQuoteFilteredDataAction(payload, 0, rowsPerPage));
        setIsFiltered(true);
      } else {
        dispatch(setDashboardData(0, rowsPerPage));
        setIsFiltered(false);
      }
    }
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleRowClickQuotes = async (quoteNumber) => {
    if (!quoteNumber) return;

    dispatch(clearGpId());

    // Find the selected quote from filteredData
    const selectedQuoteData = filteredData.find(quote => quote.quoteNumber === quoteNumber);
    if (!selectedQuoteData) {
      console.error("Selected quote not found in filtered data");
      return;
    }

    const today = dayjs().startOf("day");
    const effDate = parseQuoteDate(selectedQuoteData.effDate);

    const numericPart = quoteNumber.replace(/^Q0*/, "");

    try {
      const response = await dispatch(getGeneralPartyInfoByIdDashboard(numericPart));
      const fetchedGeneralData = response?.payload || response;

      if (fetchedGeneralData && Object.keys(fetchedGeneralData).length > 0) {
        setGeneralData(fetchedGeneralData);
        setSelectedQuote(selectedQuoteData);

        // Check if effective date is in the past
        if (effDate && effDate.isValid() && effDate.isBefore(today)) {
          setOpenDialog(true); // Open the dialog
        } else {
          // If effective date is not in the past, proceed directly to navigation
          proceedToNavigation(selectedQuoteData, fetchedGeneralData);
        }
      } else {
        console.error("No general data returned.");
      }
    } catch (error) {
      console.error("Error fetching general party info:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        status: selectedFilter,
      };
      dispatch(fetchQuoteFilteredDataAction(payload, newPage, rowsPerPage));
    } else {
      dispatch(setDashboardData(newPage, rowsPerPage));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        status: selectedFilter,
      };
      dispatch(fetchQuoteFilteredDataAction(payload, 0, newRowsPerPage));
    } else {
      dispatch(setDashboardData(0, newRowsPerPage));
    }
  };

  const handleReset = () => {
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs());
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setSelectedFilter("All");
    setIsFiltered(false);
    setPage(0);
    setFieldErrors({});
    setTouchedFields({}); 
    dispatch(setDashboardData(0, rowsPerPage));
  };

  const columnsQuotes = [
    { id: "quoteNumber", label: "Quote Number", field: "quoteNumber" },
    { id: "name", label: "Name", field: "name" },
    { id: "email", label: "Email", field: "email" },
    { id: "dob", label: "DOB", field: "dateOfBirth" },
    { id: "lic", label: "LIC NO#", field: "licenseNumber" },
    { id: "eff", label: "Eff Date", field: "effDate" },
    { id: "exp", label: "Exp Date", field: "expDate" },
    {
      id: "createdBy",
      label: "Created By",
      render: (row) => row?.createdBy?.name || "-",
    },
    {
      id: "lastModified",
      label: "Last Modified",
      render: (row) => row?.updatedBy?.name || "-",
    },
    { id: "status", label: "Status", field: "status" },
  ];

  return (
    <>
      <Box
        sx={{
          padding: isMobile ? "8px" : "10px",
          backgroundColor: "#ffffff",
          overflowX: "auto",
          minHeight: "40vh",
          "@media (min-width: 768px) and (max-width: 1024px)": {
            height: "auto",
            overflowY: "auto",
            minHeight: "55vh",
          },
          "@media (min-width: 1024px) and (min-height: 1366px)": {
            height: "auto",
            minHeight: "70vh",
          },
        }}
      >
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 2,
            alignItems: "flex-start",
            marginBottom: "12px",
            flexWrap: "wrap",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              From Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="fromDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: "55px" },
                }}
                inputProps={{ id: "fromDate", name: "fromDate" }}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              To Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="toDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: "55px" },
                }}
                inputProps={{ id: "toDate", name: "toDate" }}
              />
            </LocalizationProvider>
          </Box>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "200px",
              minWidth: isMobile ? "100%" : "200px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              Select Line of Business
            </Typography>
            <Select
              native
              value={selectedFilter}
              onChange={handleFilterChange}
              size={isMobile ? "small" : "medium"}
              sx={{
                width: "100%",
                height: "55px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100% !important",
                },
              }}
              inputProps={{ id: "selectlineofbusiness", "aria-label": "Select Line of Business" }}
            >
              <option value="All">All</option>
              <option value="Sold">Sold</option>
              <option value="Not Sold">Not Sold</option>
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              gap: isMobile ? 1 : 2,
              width: isMobile ? "100%" : "auto",
              minHeight: "85px",
              alignItems: "flex-end",
              marginTop: "-3px",
              "& button": {
                flex: isMobile ? 1 : "none",
                height: "55px",
                minHeight: "55px",
              },
            }}
          >
            <Button
              variant="contained"
              onClick={handleApply}
              id="apply-button"
              size={isMobile ? "small" : "medium"}
              disabled={!fromDate || !toDate}
            >
              Apply
            </Button>
            <Button
              onClick={handleReset}
              variant="outlined"
              id="reset-button"
              size={isMobile ? "small" : "medium"}
            >
              Reset
            </Button>
          </Box>
        </Box>
        </Box>
        {!isFiltered ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              discription="Please use the search filters above to fetch and display updated Quote data."
              message="After click Apply- results will shown as per selected filter"
            />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              title="No Data Found"
              discription="Please change search filters range where data is available as per defined range"
              message="After click Apply- results will shown as per selected filter"
            />
          </Box>
        ) : (
          <ReusableTable
            columns={columnsQuotes}
            rows={filteredData}
            loading={loading}
            error={null}
            LoaderComponent={Loader}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
            totalPages={paginationData?.totalPages || 0}
            totalElements={paginationData?.totalElements || 0}
            onPageChange={(next) => handleChangePage(null, next)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            getRowId={(r) => r.id ?? r.quoteNumber}
            onRowClick={(row) => handleRowClickQuotes(row.quoteNumber)}
            size={isMobile ? "small" : "medium"}
            tableSx={{
              minWidth: isMobile ? "600px" : "100%",
            }}
            containerSx={{
              maxHeight: isMobile ? "60vh" : "none",
              overflow: "auto",
              width: "100%",
            }}
          />
       )}

      </Box>

      {/* Effective Date Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        disableScrollLock={true}
        keepMounted={false}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            background: 'linear-gradient(145deg, #ffffff, #f9fafb)',
          },
        }}
      >
        <DialogTitle
        >
          Confirm Policy Effective Date
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#9ca3af',
              '&:hover': {
                color: '#000',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: '16px 24px',
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            The Effective Date of this quote is in the past. Some carriers may not rate.
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedOption}
              onChange={handleOptionChange}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <FormControlLabel
                value="today"
                control={
                  <Radio
                    sx={{
                      color: '#4b5e6d',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                    }}
                  />
                }
                label="Set to Today"
                sx={{
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#1a1a1a',
                  },
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              />

              <FormControlLabel
                value="setTo"
                control={
                  <Radio
                    sx={{
                      color: '#4b5e6d',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span>Set to</span>
                    <Box sx={{ flex: 1, minWidth: '150px' }}>
                      <DatePickerBot
                        entry={{
                          id: 'dialogDate',
                          startDate: customDate,
                          isPrefilled: false,
                          isEditing: true,
                        }}
                        handleInputChange={(id, field, value) => {
                          if (id === 'dialogDate' && field === 'startDate') {
                            setCustomDate(value);
                          }
                        }}
                      />
                    </Box>
                  </Box>
                }
                sx={{
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    width: '100%'
                  },
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            onClick={handleSetEffectiveDate}
          >
            Set Effective Date
          </Button>
          <Button
            variant="outlined"
            onClick={handleProceedWithExistingDate}
          >
            Keep Existing Date
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuoteTableData;